import { useMemo, useState } from 'react';

import type { SimpleFilterDefinition } from '@/sections/insights/queryBuilder/entities/types';

const UNITS = [
  { value: 'h', label: 'hours' },
  { value: 'd', label: 'days' },
  { value: 'w', label: 'weeks' },
  { value: 'M', label: 'months' },
] as const;

const RELATIVE_DATE_RE = /^now-(\d+)([dwMh])$/;

type DateMode = 'exact' | 'relative';

function parseRelativeDate(value: string): { amount: number; unit: string } {
  const match = RELATIVE_DATE_RE.exec(value);
  if (match) {
    return { amount: parseInt(match[1], 10), unit: match[2] };
  }
  return { amount: 30, unit: 'd' };
}

function formatRelativeDate(amount: number, unit: string): string {
  return `now-${amount}${unit}`;
}

interface DateValueInputProps {
  value: string | string[];
  onChange: (newValue: string | string[]) => void;
  disabled: boolean;
  filterDefinition: SimpleFilterDefinition;
}

export const DateValueInput = ({
  value,
  onChange,
  disabled,
}: DateValueInputProps) => {
  const strValue = Array.isArray(value) ? value[0] || '' : String(value);
  const valueIsRelative = RELATIVE_DATE_RE.test(strValue);

  const [mode, setMode] = useState<DateMode>(
    valueIsRelative ? 'relative' : 'exact'
  );

  const parsed = useMemo(() => parseRelativeDate(strValue), [strValue]);

  const handleModeChange = (newMode: DateMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    if (newMode === 'exact') {
      const today = new Date().toISOString().substring(0, 10);
      onChange(today);
    } else {
      onChange(formatRelativeDate(30, 'd'));
    }
  };

  const inputClassName = disabled
    ? 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-secondary cursor-not-allowed opacity-90'
    : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:border-chariot-brand-primary';

  const modeSelectClassName = disabled
    ? 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-secondary cursor-not-allowed opacity-90'
    : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:border-chariot-brand-primary';

  return (
    <div className="flex flex-[3.5] items-center gap-1">
      {mode === 'relative' ? (
        <div
          className={`flex flex-1 items-center gap-1 rounded border px-3 py-1.5 text-sm ${inputClassName}`}
        >
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={disabled}
            value={parsed.amount}
            onChange={e => {
              const num = parseInt(e.target.value, 10);
              if (num > 0) {
                onChange(formatRelativeDate(num, parsed.unit));
              }
            }}
            className="w-12 border-0 bg-transparent text-sm outline-hidden"
            aria-label="Relative date amount"
          />
          <select
            disabled={disabled}
            value={parsed.unit}
            onChange={e =>
              onChange(formatRelativeDate(parsed.amount, e.target.value))
            }
            className="border-0 bg-transparent text-sm outline-hidden"
            aria-label="Relative date unit"
          >
            {UNITS.map(u => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
          <span className="text-chariot-text-secondary text-sm">ago</span>
        </div>
      ) : (
        <input
          type="date"
          disabled={disabled}
          value={strValue}
          onChange={e => onChange(e.target.value)}
          className={`flex-1 rounded border px-3 py-1.5 text-sm ${inputClassName}`}
          aria-label="Filter value"
        />
      )}
      <select
        disabled={disabled}
        value={mode}
        onChange={e => handleModeChange(e.target.value as DateMode)}
        className={`shrink-0 rounded border px-3 py-1.5 text-sm ${modeSelectClassName}`}
        aria-label="Date mode"
      >
        <option value="exact">Exact</option>
        <option value="relative">Relative</option>
      </select>
    </div>
  );
};
